import axios from 'axios';

import { getCAFAEndpoint } from 'rdx/config';
import { CLIENT_CODE, SESSION_KEY } from 'utils';

import { proxy } from '../shared';

const { REACT_APP_NAME: application } = process.env;

const baseURL = () => {
  const { url } = getCAFAEndpoint();
  return `${proxy}${url}configuration`;
};

/**
 *  Get single configuration
 *
 * @param {string } level - operating level ['Company', 'Warehouse', 'Pos', 'User']
 * @param {string} level_id  - instance ID of the selected operating level
 * @param {string} type  - configuration type - ex. 'payment', 'scale', etc.
 * @param {string} name - configuration name - ex. 'adyen', 'netsCloud', etc.
 * @param {boolean} [lookDeeper] - should the CAFA search for same configuration in upper levels if it can not find anything for the current level
 *
 * @returns {Promise<T>}
 */
export const getConfiguration = ({
  level,
  name,
  type,
  level_id,
  lookDeeper = false,
}) => {
  const sessionKey = localStorage.getItem(SESSION_KEY);
  const clientCode = localStorage.getItem(CLIENT_CODE);
  return axios.get(baseURL(), {
    headers: {
      sessionKey,
      clientCode,
      'Look-Deeper': lookDeeper,
    },
    params: {
      application,
      level,
      level_id: level === 'Company' ? '' : level_id.toString(),
      type,
      name,
    },
  });
};
/**
 *  Get multiple configuration
 *
 * @param params {Object} - configuration query parameters
 * @param {string } [params.level] - operating level ['Company', 'Warehouse', 'Pos', 'User']
 * @param {string} [params.level_id]  - instance ID of the selected operating level
 * @param {string} [params.type]  - configuration type - ex. 'payment', 'scale', etc.
 * @param {string} [params.name] - configuration name - ex. 'adyen', 'netsCloud', etc.
 * @param {boolean} [params.lookDeeper] - should the CAFA search for same configuration in upper levels if it can not find anything for the current level
 *
 * @returns {Promise<T>}
 */
export const getBulkConfigurations = ({
  name,
  level,
  level_id,
  type,
  lookDeeper = false,
}) => {
  const sessionKey = localStorage.getItem(SESSION_KEY);
  const clientCode = localStorage.getItem(CLIENT_CODE);
  return axios.get(baseURL(), {
    headers: {
      sessionKey,
      clientCode,
      'Look-Deeper': lookDeeper,
    },
    params: {
      application,
      level,
      level_id: level === 'Company' ? '' : level_id.toString(),
      type,
      name,
    },
  });
};

/**
 *  Save single configuration
 *
 * @param params {Object} - configuration query parameters
 * @param {string } params.level - operating level ['Company', 'Warehouse', 'Pos', 'User']
 * @param {string} params.level_id  - instance ID of the selected operating level
 * @param {string} params.type  - configuration type - ex. 'payment', 'scale', etc.
 * @param {string} params.name - configuration name - ex. 'adyen', 'netsCloud', etc.
 * @param {string | number | boolean} params.value - configuration
 *
 * @returns {Promise<T>}
 */
export const saveConfiguration = ({ level, level_id, name, type, value }) => {
  const sessionKey = localStorage.getItem(SESSION_KEY);
  const clientCode = localStorage.getItem(CLIENT_CODE);
  return axios.post(
    baseURL(),
    {
      application,
      level,
      level_id: level === 'Company' ? '' : level_id.toString(),
      name,
      type,
      value,
    },
    {
      headers: {
        sessionKey,
        clientCode,
      },
    },
  );
};
/**
 *  Save single configuration
 *
 * @param params {Object} - configuration query parameters
 * @param {string } params.level - operating level ['Company', 'Warehouse', 'Pos', 'User']
 * @param {string} params.level_id  - instance ID of the selected operating level
 * @param {string} params.type  - configuration type - ex. 'payment', 'scale', etc.
 * @param {string} params.name - configuration name - ex. 'adyen', 'netsCloud', etc.
 * @param {string | number | boolean} params.value - configuration
 *
 * @returns {Promise<T>}
 */
export const updateConfiguration = ({ level, level_id, name, type, value }) => {
  const sessionKey = localStorage.getItem(SESSION_KEY);
  const clientCode = localStorage.getItem(CLIENT_CODE);
  return axios.put(
    baseURL(),
    {
      application,
      level,
      level_id: level === 'Company' ? '' : level_id.toString(),
      name,
      type,
      value,
    },
    {
      headers: {
        sessionKey,
        clientCode,
      },
    },
  );
};

/**
 *  Delete single configuration
 *
 * @param {string } level - operating level ['Company', 'Warehouse', 'Pos', 'User']
 * @param {string} level_id  - instance ID of the selected operating level
 * @param {string} type  - configuration type - ex. 'payment', 'scale', etc.
 * @param {string} name - configuration name - ex. 'adyen', 'netsCloud', etc.
 *
 * @returns {Promise<T>}
 */
export const deleteConfiguration = ({ level, name, type, level_id }) => {
  const sessionKey = localStorage.getItem(SESSION_KEY);
  const clientCode = localStorage.getItem(CLIENT_CODE);
  return axios.delete(baseURL(), {
    headers: { sessionKey, clientCode },
    params: {
      application,
      level,
      level_id: level === 'Company' ? '' : level_id.toString(),
      type,
      name,
    },
  });
};
